import React, { useEffect, useRef } from 'react';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedGroupId, selectGroup } from 'ducks/products';
import { Menu } from './menuComponents';
import { usePlannerConfig } from 'contexts/plannerConfig';
import styled from 'styled-components';
import { useCore } from 'contexts/core';
import ProductMenuTitle from './ProductMenuTitle';
import ProductsSubMenuTabs from './ProductsSubMenuTabs';
import { useAppDispatch } from 'ducks/hooks';
import Title from 'ui/Typography/Title';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { getMenuTitle } from './utils/utils';
import { resetFrontStyleSheet } from 'ducks/sheets';
import SkipButton from 'ui/SkipButton/SkipButton';
import { useHistoryEventTrigger } from 'components/History/History';
import { shouldShowProductMenuHeader } from 'ducks/layout';
import { useBehaviorTracker } from 'contexts/behaviors';
import { SubMenuType } from 'rangeConfig/@types';
import ProductsSubMenuList from './ProductsSubMenuList';

const Wrapper = styled.div`
  display: flex;
  position: sticky;
  flex-direction: column;
  top: 0;
  background: white;
  padding-top: ${({ theme }) => theme.space.l};
  padding-bottom: 0;
  z-index: 200;
`;

const WidgetWrapper = styled.div`
  border: 0;
  margin: ${({ theme }) => theme.space.l} ${({ theme }) => theme.space.xxl};
  padding: 0;
`;

const ProductsMenu: React.FunctionComponent<object> = props => {
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  useHistoryEventTrigger();

  const products = useSelector(getMarketProducts);
  const { formatMessage } = useIntl();
  const behaviorTracker = useBehaviorTracker();

  const selectedGroupId = useSelector(getSelectedGroupId);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuHeaderRef = useRef<HTMLDivElement | null>(null);
  const shouldShowHeader = useSelector(shouldShowProductMenuHeader);

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();
  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const widgets = selectedGroup?.mainMenu.widgets;
  const subMenu = selectedGroup?.mainMenu.subMenu;

  useEffect(() => {
    // scroll back to top when changing product group
    if (menuRef.current) {
      menuRef.current.scrollTop = 0;
    }
  }, [selectedGroupId]);

  const showGroupNavBar = () => {
    if (selectedGroupId) {
      behaviorTracker
        .withContext({ groupId: selectedGroupId, menuType: 'main' })
        .trigger('productGroupExited', null);
    }
    dispatch(selectGroup(''));
    if (selectedGroupId === 'frames') {
      dispatch(resetFrontStyleSheet());
    }
  };

  return (
    <Menu {...props} ref={menuRef}>
      <Wrapper>
        <SkipButton
          mx="3.5rem"
          dataTestId="skip-button-product-list-top"
          skipToId="scene-section"
          text={formatMessage({ id: 'storageone.skipButtonProductListTop' })}
        />
        {shouldShowHeader && (
          <ProductMenuTitle
            ref={menuHeaderRef}
            showGroupNavBar={showGroupNavBar}
            selectedGroup={selectedGroup}
          />
        )}
      </Wrapper>
      {widgets &&
        widgets
          .filter(w => (w.hidden ? !w.hidden(coreApi, products) : true))
          .map(({ id, title, component: Component }) => (
            <WidgetWrapper data-testid={'widget-wrapper-' + id} key={id}>
              {title && (
                <Title type={ThemeFontStyleTypeEnum.headingSmall}>
                  {getMenuTitle(title, undefined, formatMessage, coreApi)}
                </Title>
              )}
              <Component key={id} />
            </WidgetWrapper>
          ))}
      {subMenu &&
        subMenu.type === SubMenuType.TABS &&
        subMenu.options.length > 0 &&
        (subMenu.hidden ? subMenu.hidden(coreApi) : true) && (
          <ProductsSubMenuTabs
            tabs={subMenu.options}
            intitallySelectedTabId={
              subMenu.initiallySelectedTab && subMenu.initiallySelectedTab(coreApi)
            }
          />
        )}
      {subMenu && subMenu.type === SubMenuType.LIST && subMenu.options.length > 0 && (
        <ProductsSubMenuList options={subMenu.options} />
      )}
    </Menu>
  );
};

export default ProductsMenu;
